import { BB_ATTRIBUTE, BB_ATTRIBUTES_MODE, ATTR_FORM, ATTR_FIELD, ATTR_SECTION } from '/bb/constants.js';
import { getAttributesMode } from '/bb/attributes-config.js';
import bbCategoryContract from '../../constants/bb-category-contract.json';

const UNSUPPORTED_INPUT_TYPES = ['hidden', 'button', 'submit', 'reset', 'image'];
/** DOM order among `<form>` elements that have no data-rs-form, name, or id. Stable for the node once assigned. */
const SYNTHETIC_FORM_PREFIX = 'rs:form:';
/** Shared id for input, textarea, and select elements that are not inside a form. */
const UNSCOPED_FORM_ID = 'rs:unscoped';
const syntheticFormIds = new WeakMap();
const usedSyntheticFormIds = new Set();
const AUTOCOMPLETE_ELIGIBLE_FIELD_TYPES = new Set(['text', 'email', 'tel', 'password', 'search', 'url']);
const AUTOCOMPLETE_TOKENS_BY_FIELD_TYPE = new Map([
  ['date', new Set(['bday'])],
  ['month', new Set(['cc-exp'])],
  ['number', new Set(['bday-day', 'bday-month', 'bday-year', 'cc-exp-month', 'cc-exp-year'])],
]);
const FIELD_CATEGORIES = bbCategoryContract.field_categories;
const SECTION_CATEGORIES = bbCategoryContract.section_categories;

/**
 * Resolve `data-rs-*` keys + native attribute fallbacks for forms, fields, sections, pages.
 * All functions return strings or `null` (never empty strings).
 */

function trimOrNull(v) {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t.length > 0 ? t : null;
}

/** Walk up to the nearest ancestor matching the predicate (inclusive). */
function closestMatching(el, fn) {
  let cur = el;
  while (cur && cur.nodeType === 1) {
    if (fn(cur)) return cur;
    cur = cur.parentElement;
  }
  return null;
}

/**
 * Resolve `{ element, id }` for the form an element belongs to.
 * `element` is the form host (a `<form>` or annotated container).
 * A `<form>` with no data-rs-form, name, or id still resolves, with id `rs:form:N`.
 */
export function resolveForm(el) {
  if (!el || el.nodeType !== 1) return null;
  const annotated = closestMatching(el, (e) => trimOrNull(e.getAttribute && e.getAttribute(ATTR_FORM)));
  if (annotated) {
    const id = trimOrNull(annotated.getAttribute(ATTR_FORM));
    if (id) return { element: annotated, id };
  }
  const formEl = el.closest && el.closest('form');
  if (!formEl) return null;
  return { element: formEl, id: explicitFormId(formEl) || syntheticFormId(formEl) };
}

/**
 * Resolve `{ formId, fieldName, type, element }` for an input-like element.
 * A control outside any form is tracked under `rs:unscoped` when it has a name or id.
 */
export function resolveField(el) {
  if (!el || el.nodeType !== 1) return null;
  const tag = (el.tagName || '').toUpperCase();
  if (tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT') return null;
  const fieldName =
    trimOrNull(el.getAttribute(ATTR_FIELD)) || trimOrNull(el.getAttribute('name')) || trimOrNull(el.getAttribute('id'));
  if (!fieldName) return null;
  const type = resolveFieldType(el, tag);
  if (!type) return null;
  const form = resolveForm(el) || { element: el, id: UNSCOPED_FORM_ID };
  return {
    formId: form.id,
    formElement: form.element,
    fieldName,
    type,
    element: el,
    fieldCategories: resolveFieldCategories(el, fieldName),
  };
}

function explicitFormId(formEl) {
  return (
    trimOrNull(formEl.getAttribute(ATTR_FORM)) ||
    trimOrNull(formEl.getAttribute('name')) ||
    trimOrNull(formEl.getAttribute('id'))
  );
}

function syntheticFormId(formEl) {
  const cached = syntheticFormIds.get(formEl);
  if (cached) return cached;

  const forms = formElements(formEl);
  const explicitIds = new Set();
  let index = 0;
  for (let i = 0; i < forms.length; i++) {
    const form = forms[i];
    const explicitId = explicitFormId(form);
    if (explicitId) explicitIds.add(explicitId);
    if (form === formEl) break;
    if (!explicitId) index += 1;
  }

  let id = SYNTHETIC_FORM_PREFIX + index;
  while (usedSyntheticFormIds.has(id) || explicitIds.has(id)) {
    index += 1;
    id = SYNTHETIC_FORM_PREFIX + index;
  }
  usedSyntheticFormIds.add(id);
  syntheticFormIds.set(formEl, id);
  return id;
}

function formElements(formEl) {
  const doc = formEl.ownerDocument;
  if (!doc || typeof doc.getElementsByTagName !== 'function') return [];
  return doc.getElementsByTagName('form');
}

function resolveFieldType(el, tagUpper) {
  if (tagUpper === 'TEXTAREA') return 'text';
  if (tagUpper === 'SELECT') return 'select';
  const t = (el.getAttribute('type') || 'text').toLowerCase();
  if (isUnsupportedInputType(t)) return null;
  return t || 'text';
}

function isUnsupportedInputType(type) {
  return UNSUPPORTED_INPUT_TYPES.includes(type);
}

/**
 * Text-like fields may expose autocomplete whenever their attribute enables it.
 * Date/month/number fields need an explicit, type-compatible HTML autofill token;
 * this keeps native picker changes from becoming autocomplete opportunities.
 */
export function isAutocompleteEligibleField(field) {
  if (!field) return false;
  if (isTextAutocompleteField(field)) return true;

  const allowedTokens = AUTOCOMPLETE_TOKENS_BY_FIELD_TYPE.get(field.type);
  if (!allowedTokens || !field.element) return false;

  const autocomplete = field.element.getAttribute('autocomplete');
  if (!autocomplete) return false;
  return autocomplete
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .some((token) => allowedTokens.has(token));
}

/** Text-like fields have no native picker that can impersonate a replacement input. */
export function isTextAutocompleteField(field) {
  return !!field && AUTOCOMPLETE_ELIGIBLE_FIELD_TYPES.has(field.type);
}

/**
 * Native picker input events cannot be distinguished from autofill by value or
 * inputType alone. Semantic date/month/number fields therefore need the
 * browser's :-webkit-autofill animation signal instead of this input fallback.
 */
export function canDetectAutocompleteOnInput(field) {
  return isTextAutocompleteField(field);
}

/**
 * Resolve `{ element, id }` for the nearest enclosing section (data-rs-section).
 */
export function resolveSection(el) {
  if (!el || el.nodeType !== 1) return null;
  const host = closestMatching(el, (e) => trimOrNull(e.getAttribute && e.getAttribute(ATTR_SECTION)));
  if (!host) return null;
  const id = trimOrNull(host.getAttribute(ATTR_SECTION));
  if (!id) return null;
  return { element: host, id, sectionCategories: resolveSectionCategories(host, id) };
}

function resolveFieldCategories(el, fieldName) {
  const explicit = parseCategoryAttr(el.getAttribute(BB_ATTRIBUTE.FIELD_CATEGORY), Object.keys(FIELD_CATEGORIES));
  if (getAttributesMode() === BB_ATTRIBUTES_MODE.REPLACE) return explicit;

  return uniqueValues(explicit.concat(inferredCategories(fieldName, FIELD_CATEGORIES)));
}

function resolveSectionCategories(el, sectionId) {
  const explicit = parseCategoryAttr(el.getAttribute(BB_ATTRIBUTE.SECTION_CATEGORY), Object.keys(SECTION_CATEGORIES));
  if (getAttributesMode() === BB_ATTRIBUTES_MODE.REPLACE) return explicit;

  return uniqueValues(explicit.concat(inferredCategories(sectionId, SECTION_CATEGORIES)));
}

function parseCategoryAttr(rawValue, allowed) {
  const value = trimOrNull(rawValue);
  if (!value) return [];

  const allowedValues = new Set(allowed);
  const out = [];
  const parts = value.toLowerCase().split(/[,\s]+/);

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i].trim();
    if (!allowedValues.has(part) || out.includes(part)) continue;
    out.push(part);
  }

  return out;
}

function inferredCategories(value, categories) {
  const out = [];
  const names = Object.keys(categories);

  for (let i = 0; i < names.length; i++) {
    const category = names[i];
    if (nameMatchesCategory(value, categories[category])) out.push(category);
  }

  return out;
}

function nameMatchesCategory(value, tokens) {
  const lower = String(value || '').toLowerCase();

  for (let i = 0; i < tokens.length; i++) {
    if (lower === tokens[i] || lower.indexOf(tokens[i]) !== -1) return true;
  }

  return false;
}

function uniqueValues(values) {
  const out = [];

  for (let i = 0; i < values.length; i++) {
    if (!out.includes(values[i])) out.push(values[i]);
  }

  return out;
}

/** Page key = pathname (with trailing slash trimmed) + search. Hash dropped. */
export function currentPageKey() {
  if (typeof window === 'undefined' || !window.location) return '/';
  const path = (window.location.pathname || '/').replace(/\/+$/, '') || '/';
  const search = window.location.search || '';
  return path + search;
}

/** Composite map key. */
export function pageFormKey(pageKey, formId) {
  return pageKey + '::' + formId;
}

export function pageFormFieldKey(pageKey, formId, fieldName) {
  return pageKey + '::' + formId + '::' + fieldName;
}

export function pageSectionKey(pageKey, sectionId) {
  return pageKey + '::' + sectionId;
}

/** Best-effort check whether a click target is an interactive element. */
export function isInteractive(el, interactiveSelector) {
  if (!el || el.nodeType !== 1) return false;
  if (el.matches && el.matches(interactiveSelector)) return true;
  if (el.closest && el.closest(interactiveSelector)) return true;
  return false;
}
